import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ChevronSvg } from '../../Shared'

const Wrapper = styled.div``

const EmptyText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-slate);
`

const PostureCard = styled.div`
  background: var(--vo-mist);
  border: 1px solid var(--vo-cloud);
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
`

const PostureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const PostureDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--vo-success);
  flex-shrink: 0;
`

const PostureTrendLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-success);
`

const PostureDescription = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`

const CategoryPaper = styled.div`
  border-radius: 14px;
  overflow: hidden;
  background: white;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const CategoryHeader = styled.div`
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover { background: ${({ $clickable }) => ($clickable ? 'var(--vo-capri-25)' : 'transparent')}; }
`

const CategoryLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
`

const CategoryDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color || 'var(--vo-success)'};
  flex-shrink: 0;
  margin-top: 5px;
`

const CategoryInfo = styled.div`
  flex: 1;
`

const CategoryTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`

const CategoryName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--vo-ink);
`

const CategoryDescription = styled.p`
  font-family: 'Lato', sans-serif;
  margin: 0;
  font-size: 13px;
  color: var(--vo-slate);
  line-height: 1.5;
`

const SubDomainsSection = styled.div`
  border-top: 1px solid var(--vo-cloud);
  padding: 16px 20px;
  background: var(--vo-capri-25);
`

const SubDomainsTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 12px;
`

const SubDomainsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
`

const SubDomainCard = styled.div`
  padding: 12px 14px;
  background: white;
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
`

const SubDomainTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`

const SubDomainDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--vo-success);
  flex-shrink: 0;
`

const SubDomainName = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
`

const ExpandBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-slate);
  border-radius: 50%;
  flex-shrink: 0;
  &:hover { background: var(--vo-mist); }
`

const BadgePill = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
`

const getStatusColor = (label) => {
  if (!label) return 'var(--vo-slate)'
  const l = label.toLowerCase()
  if (l.includes('implement') || l.includes('operational') || l.includes('complete')) return 'var(--vo-success)'
  if (l.includes('progress') || l.includes('developing')) return 'var(--vo-celeste)'
  return 'var(--vo-slate)'
}

export const GovernanceStructure = ({ categories = [], overallPosture, showSubDomains = true }) => {
  const { t } = useTranslation('transparency')
  const [expandedCategory, setExpandedCategory] = useState(null)

  const toggleCategory = (categoryId) => {
    if (showSubDomains) {
      setExpandedCategory(expandedCategory === categoryId ? null : categoryId)
    }
  }

  if (!categories || categories.length === 0) {
    return <EmptyText>{t('protected.emptyGovernance')}</EmptyText>
  }

  return (
    <Wrapper>
      {overallPosture && (
        <PostureCard>
          <PostureRow>
            <PostureDot />
            <PostureTrendLabel>{overallPosture.statusLabel || overallPosture.label}</PostureTrendLabel>
            {overallPosture.maturityLabel && (
              <BadgePill $color="var(--vo-success)" $bg="var(--vo-success-10)">{overallPosture.maturityLabel}</BadgePill>
            )}
            {overallPosture.description && (
              <PostureDescription>{overallPosture.description}</PostureDescription>
            )}
          </PostureRow>
        </PostureCard>
      )}

      <CategoriesGrid>
        {categories.map((category, index) => {
          const hasSubDomains = category.subDomains?.length > 0
          const isClickable = showSubDomains && hasSubDomains
          const dotColor = getStatusColor(category.statusLabel)

          return (
            <CategoryPaper key={category.id || index}>
              <CategoryHeader
                $clickable={isClickable}
                onClick={() => toggleCategory(category.id)}
              >
                <CategoryLeft>
                  <CategoryDot $color={dotColor} />
                  <CategoryInfo>
                    <CategoryTop>
                      <CategoryName>{category.name}</CategoryName>
                      {category.statusLabel && (
                        <BadgePill
                          $color={dotColor}
                          $bg={`${dotColor}1A`}
                        >
                          {category.statusLabel}
                        </BadgePill>
                      )}
                    </CategoryTop>
                    {category.description && (
                      <CategoryDescription>{category.description}</CategoryDescription>
                    )}
                  </CategoryInfo>
                </CategoryLeft>
                {isClickable && (
                  <ExpandBtn>
                    <ChevronSvg $expanded={expandedCategory === category.id} />
                  </ExpandBtn>
                )}
              </CategoryHeader>

              {showSubDomains && hasSubDomains && expandedCategory === category.id && (
                <SubDomainsSection>
                  <SubDomainsTitle>{t('protected.governance.subdomains')}</SubDomainsTitle>
                  <SubDomainsGrid>
                    {category.subDomains.map((subDomain, subIndex) => (
                      <SubDomainCard key={subDomain.id || subIndex}>
                        <SubDomainTop>
                          <SubDomainDot />
                          {subDomain.maturityLabel && (
                            <BadgePill $color="var(--vo-success)" $bg="var(--vo-success-10)">{subDomain.maturityLabel}</BadgePill>
                          )}
                        </SubDomainTop>
                        <SubDomainName>{subDomain.name}</SubDomainName>
                      </SubDomainCard>
                    ))}
                  </SubDomainsGrid>
                </SubDomainsSection>
              )}
            </CategoryPaper>
          )
        })}
      </CategoriesGrid>
    </Wrapper>
  )
}
